import React, { useCallback, useEffect, useState } from 'react'

// ---- Config ---------------------------------------------------------------
// Adjust this to match your actual backend base URL / proxy setup.
const API_BASE = '/api/tasks'

// Status options for the dropdown. Line up this list with whatever your
// backend's status list actually contains (confirm with backend team).
const STATUS_OPTIONS = [
  'OPEN',
  'IN_PROGRESS',
  'ON_HOLD',
  'CLOSED',
  'REOPENED',
]

const Publicpage = () => {
  // --- Read taskId, token, who from the URL query string -------------------
  const params = new URLSearchParams(window.location.search)
  const taskId = params.get('taskId')
  const token = params.get('token')
  const who = params.get('who')

  const hasRequiredParams = Boolean(taskId && token && who)

  const [loading, setLoading] = useState(true)
  const [task, setTask] = useState(null)
  const [loadError, setLoadError] = useState(null)

  const [newStatus, setNewStatus] = useState('')
  const [remark, setRemark] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const [justUpdated, setJustUpdated] = useState(false)

  // --- 5.1 View Task via Link ----------------------------------------------
  const fetchTask = useCallback(async () => {
    if (!hasRequiredParams) return

    setLoading(true)
    setLoadError(null)

    try {
      const url = `${API_BASE}/public/view/${encodeURIComponent(
        taskId
      )}?token=${encodeURIComponent(token)}&who=${encodeURIComponent(who)}`

      const res = await fetch(url, { method: 'GET' })

      if (!res.ok) {
        // 403 -> { message, error } shape per the docs
        const body = await res.json().catch(() => null)
        throw new Error(
          body?.error || 'This link is invalid or you do not have access.'
        )
      }

      const data = await res.json()
      setTask(data)
      setNewStatus(data.statusName || '')
    } catch (err) {
      setTask(null)
      setLoadError(err.message || 'Failed to load task')
    } finally {
      setLoading(false)
    }
  }, [hasRequiredParams, taskId, token, who])

  useEffect(() => {
    if (!hasRequiredParams) {
      setLoading(false)
      return
    }
    fetchTask()
  }, [hasRequiredParams, fetchTask])

  // --- 5.2 Update Status via Link -------------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!newStatus) return

    setSubmitting(true)
    setSubmitError(null)
    setJustUpdated(false)

    try {
      const url = `${API_BASE}/public/updateStatus/${encodeURIComponent(
        taskId
      )}?token=${encodeURIComponent(token)}&who=${encodeURIComponent(who)}`

      const res = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newStatus, remark }),
      })

      if (!res.ok) {
        const body = await res.json().catch(() => null)
        throw new Error(
          body?.error || 'Could not update the status. Please try again.'
        )
      }

      // Success -> re-fetch to show the updated status/history
      setRemark('')
      setJustUpdated(true)
      await fetchTask()
    } catch (err) {
      setSubmitError(err.message || 'Something went wrong.')
    } finally {
      setSubmitting(false)
    }
  }

  // --- Missing params: never call the API, show error immediately ----------
  if (!hasRequiredParams) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <h2 style={styles.errorTitle}>Link incomplete</h2>
          <p style={styles.errorText}>
            This link is missing required information and can&apos;t be
            opened. Please use the link from your task email.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        {loading && <p style={styles.muted}>Loading task…</p>}

        {!loading && loadError && (
          <>
            <h2 style={styles.errorTitle}>This link is invalid or you don&apos;t have access</h2>
            <p style={styles.errorText}>{loadError}</p>
          </>
        )}

        {!loading && !loadError && task && (
          <>
            <header style={styles.header}>
              <h1 style={styles.subject}>{task.subject}</h1>
              <span style={styles.statusBadge}>{task.statusName}</span>
            </header>

            {task.description && (
              <p style={styles.description}>{task.description}</p>
            )}

            <dl style={styles.metaGrid}>
              {task.priority && (
                <div style={styles.metaItem}>
                  <dt style={styles.metaLabel}>Priority</dt>
                  <dd style={styles.metaValue}>{task.priority}</dd>
                </div>
              )}
              {task.endDate && (
                <div style={styles.metaItem}>
                  <dt style={styles.metaLabel}>Due date</dt>
                  <dd style={styles.metaValue}>{task.endDate}</dd>
                </div>
              )}
              {task.labelName && (
                <div style={styles.metaItem}>
                  <dt style={styles.metaLabel}>Label</dt>
                  <dd style={styles.metaValue}>{task.labelName}</dd>
                </div>
              )}
            </dl>

            <hr style={styles.divider} />

            <form onSubmit={handleSubmit} style={styles.form}>
              <h2 style={styles.formTitle}>Update status</h2>

              <label style={styles.label} htmlFor="statusSelect">
                New status
              </label>
              <select
                id="statusSelect"
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                style={styles.select}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>

              <label style={styles.label} htmlFor="remarkInput">
                Remark
              </label>
              <textarea
                id="remarkInput"
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                placeholder="Add a note about this update (optional)"
                rows={3}
                style={styles.textarea}
              />

              {submitError && <p style={styles.errorText}>{submitError}</p>}
              {justUpdated && (
                <p style={styles.successText}>Status updated successfully.</p>
              )}

              <button type="submit" disabled={submitting} style={styles.button}>
                {submitting ? 'Updating…' : 'Update status'}
              </button>
            </form>

            {Array.isArray(task.statusUpdates) && task.statusUpdates.length > 0 && (
              <>
                <hr style={styles.divider} />
                <h2 style={styles.formTitle}>History</h2>
                <ul style={styles.historyList}>
                  {task.statusUpdates
                    .slice()
                    .reverse()
                    .map((update, idx) => (
                      <li key={idx} style={styles.historyItem}>
                        <span style={styles.historyStatus}>
                          {update.oldStatus} → {update.newStatus}
                        </span>
                        <span style={styles.historyMeta}>
                          {update.updatedByName || update.updatedById} ·{' '}
                          {update.updatedAt || update.date || ''}
                        </span>
                        {update.remark && (
                          <p style={styles.historyRemark}>{update.remark}</p>
                        )}
                      </li>
                    ))}
                </ul>
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#f5f6f8',
    display: 'flex',
    justifyContent: 'center',
    padding: '32px 16px',
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  card: {
    background: '#fff',
    borderRadius: 12,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    padding: 28,
    width: '100%',
    maxWidth: 560,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
  },
  subject: { fontSize: 20, fontWeight: 600, margin: 0, color: '#1a1a1a' },
  statusBadge: {
    fontSize: 12,
    fontWeight: 600,
    padding: '4px 10px',
    borderRadius: 999,
    background: '#eef2ff',
    color: '#4338ca',
    whiteSpace: 'nowrap',
  },
  description: { marginTop: 12, color: '#444', lineHeight: 1.5 },
  metaGrid: {
    display: 'flex',
    gap: 20,
    marginTop: 16,
    flexWrap: 'wrap',
  },
  metaItem: { minWidth: 90 },
  metaLabel: { fontSize: 11, textTransform: 'uppercase', color: '#888', margin: 0 },
  metaValue: { fontSize: 14, color: '#222', margin: '2px 0 0' },
  divider: { border: 'none', borderTop: '1px solid #eee', margin: '20px 0' },
  form: { display: 'flex', flexDirection: 'column', gap: 8 },
  formTitle: { fontSize: 15, fontWeight: 600, margin: '0 0 4px' },
  label: { fontSize: 13, color: '#555', marginTop: 6 },
  select: {
    padding: '8px 10px',
    borderRadius: 8,
    border: '1px solid #ddd',
    fontSize: 14,
  },
  textarea: {
    padding: '8px 10px',
    borderRadius: 8,
    border: '1px solid #ddd',
    fontSize: 14,
    resize: 'vertical',
  },
  button: {
    marginTop: 8,
    padding: '10px 16px',
    background: '#4338ca',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  muted: { color: '#888' },
  errorTitle: { color: '#b91c1c', fontSize: 18, marginBottom: 6 },
  errorText: { color: '#b91c1c', fontSize: 14 },
  successText: { color: '#15803d', fontSize: 14 },
  historyList: { listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 },
  historyItem: { borderLeft: '2px solid #eee', paddingLeft: 10 },
  historyStatus: { fontSize: 13, fontWeight: 600, color: '#333' },
  historyMeta: { display: 'block', fontSize: 12, color: '#999' },
  historyRemark: { fontSize: 13, color: '#555', margin: '4px 0 0' },
}

export default Publicpage